import { isUrl } from '@shared/all/validators';

import formatDate from './formatDate';

// What a tag carries that a post's other factiii chips don't show: a number,
// text that isn't a link (links have their own panel), and the date a
// TIME_REQUIRED tag recorded. Web and mobile list the same rows from this.
export type TagValue = { factiiiId: number; label: string };

type TagEntry<SpaceTime = object> = {
  data: string | null;
  number: number | null;
  spaceTimes: (SpaceTime & { timestamp: Date | string | null })[];
};

// No separator below ten thousand. A tag's number carries no unit, and the
// four-digit ones are mostly years ("2,026"), calorie counts and codes, which
// read wrong grouped; an amount that size reads fine without it.
const formatNumber = (n: number): string =>
  Math.abs(n) < 10_000 ? String(n) : n.toLocaleString();

const entryLabel = (pf: TagEntry, timeZone?: string): string | null => {
  const stamp = pf.spaceTimes.find((st) => st.timestamp)?.timestamp;
  const parts = [
    pf.number !== null ? formatNumber(pf.number) : null,
    pf.data && !isUrl(pf.data) ? pf.data.trim() : null,
    stamp ? formatDate(stamp, timeZone) : null
  ].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : null;
};

export const tagValues = (factiiiId: number, entries: TagEntry[]): TagValue[] =>
  entries.flatMap((pf) => {
    const label = entryLabel(pf);
    return label ? [{ factiiiId, label }] : [];
  });

// Oakbox's document screens have no map and no links panel, so a source's
// link and a place are listed as text after the values.
//
// Dates in UTC: every tag here was read off a document, whose dates the tagger
// stores as the day written in UTC (apps/server/src/helpers/documentDate.ts).
// In the reader's own zone they showed a day early west of Greenwich.
export const tagDetails = (
  entries: TagEntry<{ display: string | null }>[]
): string[] => [
  ...entries.flatMap((pf) => entryLabel(pf, 'UTC') ?? []),
  ...entries.flatMap((pf) => (pf.data && isUrl(pf.data) ? pf.data : [])),
  ...entries.flatMap((pf) =>
    pf.spaceTimes.flatMap((st) => st.display?.trim() || [])
  )
];
